/**
 * HustlePointsWalletPage — /points
 *
 * Member-facing wallet:
 *  · Big balance card (pts + AUD equivalent + inactivity countdown)
 *  · Top-up form (buttons for $25 / $50 / $100 / $250 or custom)
 *  · Gift Pack form (send to email + message)
 *  · Ledger table (last 50 entries)
 *  · Prominent "How it works" band + link to legal terms
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Sparkles, Gift, ArrowRight, Clock, Zap, ShieldCheck } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";
const withCreds = (extra = {}) => ({ credentials: "include", ...extra });

const TOPUP_PRESETS = [25, 50, 100, 250];

export default function HustlePointsWalletPage() {
  const { user, checking } = useAuth();
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [topupAud, setTopupAud] = useState(50);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("topup"); // topup | gift
  const [gift, setGift] = useState({
    amount_aud: 50, to_email: "", to_name: "",
    from_name: "", message: "",
  });

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/api/hustlepoints/balance`, withCreds())
      .then((r) => r.ok ? r.json() : null)
      .then((j) => j && setBalance(j))
      .catch(() => {});
    fetch(`${API}/api/hustlepoints/ledger?limit=50`, withCreds())
      .then((r) => r.ok ? r.json() : { entries: [] })
      .then((j) => setLedger(j.entries || []))
      .catch(() => {});
  }, [user]);

  const topup = async (e) => {
    e && e.preventDefault();
    if (!topupAud || topupAud < 1) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/hustlepoints/topup`,
        withCreds({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount_aud: Number(topupAud),
            return_url: `${window.location.origin}/points?topup=success`,
          }),
        }));
      const j = await r.json();
      if (r.ok && j.checkout_url) {
        window.location.href = j.checkout_url;
      } else {
        toast.error(j.detail || "Could not start top-up.");
      }
    } finally {
      setBusy(false);
    }
  };

  const buyGift = async (e) => {
    e && e.preventDefault();
    if (!gift.amount_aud || gift.amount_aud < 1) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/hustlepoints/giftpack/buy`,
        withCreds({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...gift,
            amount_aud: Number(gift.amount_aud),
            return_url: `${window.location.origin}/points/giftpack/success`,
          }),
        }));
      const j = await r.json();
      if (r.ok && j.checkout_url) {
        window.location.href = j.checkout_url;
      } else {
        toast.error(j.detail || "Could not start gift-pack purchase.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-stone-50">
      <div className="text-stone-500">Loading…</div>
    </div>;
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-stone-50">
        <PlatformHeader brand={{ name: "Hustle Points", to: "/points" }} showBookCta={false} showCart={false} />
        <div className="max-w-md mx-auto mt-24 rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-sm">
          <Coins size={38} className="text-amber-500 mx-auto mb-4"/>
          <h1 className="text-2xl font-black mb-2">Hustle Points</h1>
          <p className="text-sm text-stone-600 mb-6">
            Sign in to see your points balance, top up, and gift points to friends.
          </p>
          <Link to={`/login?next=/points`}
                data-testid="points-signin-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black">
            Sign in <ArrowRight size={14}/>
          </Link>
        </div>
        <SuiteFooter />
      </div>
    );
  }

  const pts = balance?.balance_pts ?? 0;
  const aud = balance?.balance_aud ?? 0;
  const forfeitAt = balance?.forfeit_at;

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-[#0a0908]" data-testid="points-wallet-page">
      <PlatformHeader brand={{ name: "Hustle Points", to: "/points" }} showBookCta={false} showCart={false} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-8">

        {/* Balance card */}
        <section
          data-testid="points-balance"
          className="rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 text-slate-950 p-8 shadow-xl"
        >
          <div className="flex items-center gap-2 text-xs font-black tracking-widest uppercase">
            <Coins size={16}/> Your Hustle Points
          </div>
          <div className="mt-3 flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-6xl font-black leading-none">{pts.toLocaleString("en-AU")}</div>
              <div className="text-sm font-bold mt-2 opacity-80">
                = ~${aud.toFixed(2)} at any Hustle Vendor
              </div>
            </div>
            {forfeitAt && (
              <div className="ml-auto text-right text-xs opacity-90">
                <Clock size={13} className="inline mb-0.5 mr-1"/>
                Forfeit if inactive by
                <div className="font-bold">{new Date(forfeitAt).toLocaleDateString("en-AU")}</div>
              </div>
            )}
          </div>
        </section>

        {/* How it works strip */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          {[
            [Zap, "10% top-up bonus", "$100 becomes 110 points. That's an instant 10% loyalty return."],
            [Sparkles, "10 pts = $1 in-store", "Spend at any participating Hustle Vendor. No fractional cents."],
            [ShieldCheck, "Loyalty, not stored value", "Non-cash, non-refundable, forfeits after 24 months of inactivity."],
          ].map(([I, title, body], i) => (
            <div key={i} className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
              <I size={18} className="text-amber-500 mb-2"/>
              <div className="font-black text-stone-900 dark:text-stone-100">{title}</div>
              <div className="text-stone-600 dark:text-stone-400 mt-1 leading-relaxed">{body}</div>
            </div>
          ))}
        </section>

        {/* Tab switcher: top up / gift */}
        <section className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-8 shadow-sm">
          <div className="flex gap-2 mb-6" role="tablist">
            <button data-testid="points-tab-topup"
                    onClick={() => setTab("topup")}
                    className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "topup" ? "bg-amber-500 text-slate-950" : "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400"}`}>
              <Coins size={14} className="inline mr-1.5 mb-0.5"/> Top up
            </button>
            <button data-testid="points-tab-gift"
                    onClick={() => setTab("gift")}
                    className={`px-4 py-2 rounded-full text-sm font-bold ${tab === "gift" ? "bg-amber-500 text-slate-950" : "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400"}`}>
              <Gift size={14} className="inline mr-1.5 mb-0.5"/> Gift a Pack
            </button>
          </div>

          {tab === "topup" && (
            <form onSubmit={topup} data-testid="points-topup-form">
              <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
                Buy Hustle Points at a 10% loyalty bonus.
                <span className="text-amber-600 dark:text-amber-400 font-bold ml-1">
                  ${Number(topupAud || 0).toFixed(2)} → {Math.round(Number(topupAud || 0) * 1.1)} pts
                </span>
              </p>
              <div className="flex gap-2 flex-wrap mb-4">
                {TOPUP_PRESETS.map((n) => (
                  <button key={n} type="button"
                          onClick={() => setTopupAud(n)}
                          data-testid={`points-preset-${n}`}
                          className={`px-4 py-2 rounded-full font-bold text-sm ${Number(topupAud) === n ? "bg-amber-500 text-slate-950" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                    ${n} → {Math.round(n * 1.1)} pts
                  </button>
                ))}
              </div>
              <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                Custom amount (AUD)
              </label>
              <input
                type="number" min="1" max="10000" step="1"
                value={topupAud}
                onChange={(e) => setTopupAud(e.target.value)}
                data-testid="points-topup-amount"
                className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 focus:outline-none focus:border-amber-400 text-stone-900 dark:text-stone-100"
              />
              <button type="submit" disabled={busy}
                      data-testid="points-topup-submit"
                      className="w-full mt-4 px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black disabled:opacity-50">
                {busy ? "Redirecting to Stripe…" : "Buy points →"}
              </button>
              <p className="mt-3 text-[11px] text-stone-500 leading-relaxed">
                Paid securely by Stripe. Points credit instantly once your card clears.
                By buying you agree to the <Link to="/legal/hustlepoints-terms" className="underline text-amber-600">Hustle Points Program Terms</Link>.
              </p>
            </form>
          )}

          {tab === "gift" && (
            <form onSubmit={buyGift} data-testid="points-gift-form">
              <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
                Send a Hustle Points Gift Pack. Recipient claims the code, points land in their account.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                    Amount (AUD)
                  </label>
                  <input type="number" min="10" max="1000" step="1"
                         value={gift.amount_aud}
                         onChange={(e) => setGift({ ...gift, amount_aud: e.target.value })}
                         data-testid="points-gift-amount"
                         className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                    Recipient email
                  </label>
                  <input type="email"
                         value={gift.to_email}
                         onChange={(e) => setGift({ ...gift, to_email: e.target.value })}
                         data-testid="points-gift-to-email"
                         placeholder="friend@example.com"
                         className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                    From (name)
                  </label>
                  <input type="text"
                         value={gift.from_name}
                         onChange={(e) => setGift({ ...gift, from_name: e.target.value })}
                         data-testid="points-gift-from"
                         placeholder="Your name"
                         className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700"/>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                    Recipient name
                  </label>
                  <input type="text"
                         value={gift.to_name}
                         onChange={(e) => setGift({ ...gift, to_name: e.target.value })}
                         data-testid="points-gift-to-name"
                         placeholder="Friend's name"
                         className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700"/>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
                    Personal message (optional)
                  </label>
                  <textarea rows={2} maxLength={500}
                            value={gift.message}
                            onChange={(e) => setGift({ ...gift, message: e.target.value })}
                            data-testid="points-gift-message"
                            placeholder="Happy birthday — coffee's on me at any Hustle café."
                            className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700"/>
                </div>
              </div>
              <button type="submit" disabled={busy}
                      data-testid="points-gift-submit"
                      className="w-full mt-4 px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black disabled:opacity-50">
                {busy ? "Redirecting to Stripe…" : "Buy Gift Pack →"}
              </button>
              <p className="mt-3 text-[11px] text-stone-500 leading-relaxed">
                Gift Packs are valid for 24 months from purchase. If unclaimed by then,
                points are forfeited under the <Link to="/legal/hustlepoints-terms" className="underline text-amber-600">Program Terms</Link>.
              </p>
            </form>
          )}
        </section>

        {/* Ledger */}
        <section className="rounded-3xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-8 shadow-sm">
          <h3 className="text-xl font-black mb-4">Recent activity</h3>
          {ledger.length === 0 ? (
            <div data-testid="points-ledger-empty"
                 className="text-sm text-stone-500 text-center py-6">
              No activity yet. Top up above to get started.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="points-ledger-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-200 dark:border-stone-800">
                  <th className="py-2">When</th>
                  <th className="py-2">Reason</th>
                  <th className="py-2 text-right">Δ pts</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((l, i) => (
                  <tr key={i} data-testid={`points-ledger-row-${i}`}
                      className="border-b border-stone-100 dark:border-stone-800">
                    <td className="py-2 text-stone-500">
                      {l.at ? new Date(l.at).toLocaleString("en-AU", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="py-2">{l.reason}</td>
                    <td className={`py-2 text-right font-bold ${l.delta_pts >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {l.delta_pts >= 0 ? "+" : ""}{l.delta_pts.toLocaleString("en-AU")}
                    </td>
                    <td className="py-2 text-right text-stone-500">{l.balance_after.toLocaleString("en-AU")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <p className="text-center text-[11px] text-stone-500">
          Hustle Points are a loyalty reward, not stored value. Read the full{" "}
          <Link to="/legal/hustlepoints-terms" className="underline text-amber-600" data-testid="points-terms-link">
            Program Terms
          </Link>.
        </p>
      </div>
      <SuiteFooter />
    </div>
  );
}
